/* eslint-disable func-names */
import * as Yup from "yup";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import Typography from "@material-ui/core/Typography";
import React, { FC, useState } from "react";
import { Field, Form, Formik, FormikHelpers } from "formik";
import { useDispatch } from "react-redux";

import BaseInput from "../../../../../components/elements/BaseInput";
import Button from "../../../../../components/elements/Button";
import EditCustomDomainListDashboard from "../EditCustomDomainListDashboard";
import FormFieldError from "../../../../../components/elements/FormFieldError";
import classes from "../../PublicationListSelectionSlide.module.scss";
import { BrandgenListDomain, BrandgenPublicationList } from "../../../../../types/campaignTypes";
import { errorToaster, successToaster } from "../../../../../helpers/toasters";
import { setUpdatedCustomDomainLists } from "../../../../../redux/reducers/campaignToCreate";
import { trimDomains } from "../../helpers";

type FormInitialValues = {
  name: string;
  domainsToUpdate: BrandgenListDomain[];
  newDomains: string;
};

type EditCustomDomainListModalProps = {
  isOpen: boolean;
  handleClose: () => void;
  customDomainListToEdit: BrandgenPublicationList | null;
  updateDomainList: (
    listId: string,
    listName: string,
    domainsToUpdate: BrandgenListDomain[],
    domainsToAdd: string[],
    saveCreatedDomainList: (updatedDomainLists: BrandgenPublicationList[]) => void,
  ) => void;
};

const EditCustomDomainListModal: FC<EditCustomDomainListModalProps> = ({
  isOpen,
  handleClose,
  customDomainListToEdit,
  updateDomainList,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const formInitialValues: FormInitialValues = {
    name: customDomainListToEdit?.name as string,
    domainsToUpdate: customDomainListToEdit?.listDomains ?? [],
    newDomains: "",
  };

  const validationSchema = Yup.object({
    newDomains: Yup.string().test("newDomainsValidation", function (newDomains) {
      const { path, createError } = this;

      if (newDomains) {
        const trimmedDomains = newDomains
          .replaceAll("\n", " ")
          .split(" ")
          .map((domain: string) => domain.replace(",", "").trim())
          .filter((str) => str);

        const domainRegex = /^(?:(?:https?|ftp):\/\/)?(?:www\.)?[\d.a-z-]{3,}\.[a-z]{2,}(?::\d{1,5})?(?:\/.*)?$/i;

        for (const domain of trimmedDomains) {
          if (!domainRegex.test(domain)) {
            return createError({
              path,
              message:
                "Some of your domains are not valid! Consider that domains should be without HTTP/HTTPS protocol! And have schema like 'example.com'",
            });
          }
        }
        return true;
      }
      return true;
    }),
    domainsToUpdate: Yup.array().test("domainsToUpdateValidation", function (domainsToUpdate) {
      const { path, createError } = this;

      if (domainsToUpdate) {
        const domainRegex = /^(?:(?:https?|ftp):\/\/)?(?:www\.)?[\d.a-z-]{3,}\.[a-z]{2,}(?::\d{1,5})?(?:\/.*)?$/i;

        for (const domain of domainsToUpdate) {
          if (!domainRegex.test(domain.name!) || !domain.name) {
            return createError({
              path,
              message:
                "Some of your domains are not valid! Consider that domains should be without HTTP/HTTPS protocol! And have schema like 'example.com'",
            });
          }
        }

        const removedDuplicateDomains = new Set(domainsToUpdate.map((domain) => domain.name));

        if (removedDuplicateDomains.size !== domainsToUpdate.length) {
          return createError({
            path,
            message: "Some of your domains are not unique! Consider that each domain should be unique!",
          });
        }

        return true;
      }
      return createError({
        path,
        message: "Required field.",
      });
    }),
  });

  const dispatch = useDispatch();

  const handleSubmit = async (values: FormInitialValues, actions: FormikHelpers<FormInitialValues>) => {
    const saveUpdatedDomainListToStore = (updatedCustomDomainLists: BrandgenPublicationList[]) => {
      dispatch(setUpdatedCustomDomainLists(updatedCustomDomainLists));
      setIsLoading(false);
      actions.resetForm();
      if (customDomainListToEdit) {
        successToaster(`Domain List "${values.name}" is successfully updated!`);
      }
      handleClose();
    };

    try {
      setIsLoading(true);
      const { name, newDomains, domainsToUpdate } = values;

      const trimmedDomains = trimDomains(newDomains);
      const uniqueDomains = [...new Set(trimmedDomains)];

      const { id: listId } = customDomainListToEdit!;

      await updateDomainList(listId, name, domainsToUpdate, uniqueDomains, saveUpdatedDomainListToStore);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("CreateCustomDomainListModal error: ", error);
      setIsLoading(false);
      errorToaster((error as Error)?.message ?? String(error), 7000);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={isLoading ? () => null : handleClose}
      aria-labelledby="Custom Domain List Edit Modal"
      className={classes.dialogContainer}
      maxWidth="md"
      fullWidth
    >
      <Formik
        initialValues={formInitialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        validateOnBlur={false}
      >
        {({ values, setFieldValue }) => (
          <>
            <div className={classes.dialogTitle}>
              <Typography variant="h2">Edit Publication List</Typography>
            </div>

            <Form>
              <DialogContent className={classes.dialogContent}>
                <div className={classes.input}>
                  <Field
                    as={BaseInput}
                    value={values.name}
                    name="name"
                    variant="outlined"
                    label="Enter name of your list *"
                  />

                  <FormFieldError name="name" />
                </div>

                <div className={classes.editCustomDomainListDashboardSection}>
                  <EditCustomDomainListDashboard domains={values.domainsToUpdate} setFormikFieldValue={setFieldValue} />
                  {/* <FormFieldError>{errors.domainsToUpdate}</FormFieldError> */}
                  <FormFieldError name="domainsToUpdate" />
                </div>

                <div className={classes.textArea}>
                  <Field
                    as={BaseInput}
                    value={values.newDomains}
                    name="newDomains"
                    variant="outlined"
                    label="Enter a list of new domains (website addresses), separated by space or comma, to add to a list  *"
                    rows={4}
                    multiline
                  />

                  <FormFieldError name="newDomains" />
                </div>
              </DialogContent>
              <DialogActions>
                <Button className={classes.button} onClick={isLoading ? () => null : handleClose} variant="outlined">
                  Cancel
                </Button>
                <Button className={classes.button} type="submit" isLoading={isLoading} loaderSize={25}>
                  Submit
                </Button>
              </DialogActions>
            </Form>
          </>
        )}
      </Formik>
    </Dialog>
  );
};

export default EditCustomDomainListModal;
